import { HeroProps, NavItem } from '../../../shared/data-models';
import '../../../tailwind.css';
import React, { HTMLAttributes } from 'react';

export interface AnnouncementHeroProps
  extends HeroProps,
    HTMLAttributes<HTMLDivElement> {
  /** the announcement at the top of the hero */
  announcement?: NavItem;
  /** the h1 text in the hero */
  h1: string;
  /** the p text in the hero */
  p: string;
  /** the first button in the hero */
  primaryButton?: NavItem;
  /** the second button in the hero */
  secondaryButton?: NavItem;
  /** custom content */
  children?: any;
}

// Please do not use types off of a default export module or else Storybook Docs will suffer.
// see: https://github.com/storybookjs/storybook/issues/9556
/**
 * A custom AnnouncementHero component
 * Provides a hero with an announcement, h1, p, and two buttons
 * It is  styled to match the TestifySec brand
 * It is responsive
 * It is accessible
 * It supports dark mode (to test or render in eithrer dark or light mode, change your browser setting for prefers-color-scheme)
 */
export const AnnouncementHero = ({
  announcement,
  h1,
  p,
  primaryButton,
  secondaryButton,
}: AnnouncementHeroProps) => {
  return (
    <div className="relative isolate px-6 pt-14 lg:px-8">
      <div
        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
      <div className="mx-auto max-w-md py-24 sm:py-48 lg:py-56">
        <div className="hidden sm:mb-8 sm:flex sm:justify-center">
          {announcement && (
            <div className="relative rounded-full px-3 py-1 text-sm leading-6 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
              {announcement.label}{' '}
              <a
                href={announcement.href}
                className="font-semibold text-testify-blue dark:text-testify-orange"
              >
                <span className="absolute inset-0" aria-hidden="true" />
                Read more <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          )}
        </div>
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-testify-orange sm:text-6xl">
            {h1}
          </h1>
          <p className="mt-6 text-lg leading-8">{p}</p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            {primaryButton && (
              <a
                href={primaryButton.href}
                className="rounded-md bg-testify-blue dark:bg-testify-orange px-3.5 py-2.5 text-sm font-semibold text-testify-orange dark:text-testify-blue shadow-sm hover:bg-testify-orange hover:text-testify-blue dark:hover:bg-testify-almond focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {primaryButton.label}
              </a>
            )}
            {secondaryButton && (
              <a
                href={secondaryButton.href}
                className="text-sm font-semibold leading-6 text-gray-900 dark:text-testify-orange"
              >
                {secondaryButton.label} <span aria-hidden="true">→</span>
              </a>
            )}
          </div>
        </div>
      </div>
      <div
        className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </div>
  );
};
